import { Head, Link, router } from "@inertiajs/react"
import { ChevronLeft, ChevronRight, Pencil } from "lucide-react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { addDays, parseLocalDate } from "@/lib/date-utils"
import {
  calendarPath,
  casePath,
  customerLogPath,
  customerPath,
  marketingActivityPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

// --- Types ---

interface TeamMember {
  name: string | null
  is_leader: boolean
}

interface Schedule {
  id: number
  type: "case" | "customer"
  case_id: number
  customer_id: number | null
  ref_name: string | null
  ref_id: number
  time_desc: string | null
  title: string | null
  notes: string | null
  team: TeamMember[]
  can_edit: boolean
}

interface Holiday {
  id: number
  person_name: string | null
  description: string | null
  event_type: string | null
  added_by: string | null
}

interface MarketingLog {
  id: number
  customer_name: string | null
  customer_id: number | null
  log_type_name: string | null
  contact: string | null
  employee_initials: string | null
  title: string | null
  notes: string | null
}

interface MarketingActivity {
  id: number
  title: string | null
  type_name: string | null
  notes: string | null
  team: string[]
}

interface AvailabilityEntry {
  user_id: number
  name: string
  available: boolean
  pto_type: "P" | "A" | null
  notes: string | null
}

interface CalendarDay {
  date: string
  day_name: string
  display_date: string
  is_today: boolean
  in_month: boolean
  holidays: Holiday[]
  schedules: Schedule[]
  marketing_logs: MarketingLog[]
  marketing_activities: MarketingActivity[]
  availability: AvailabilityEntry[]
}

interface FilterOption {
  id: number
  name: string
}

interface Filters {
  case_id: number | null
  employee_id: number | null
}

interface Permissions {
  can_filter: boolean
  can_edit_schedule: boolean
  can_view_customers: boolean
}

interface Props {
  week_days: CalendarDay[]
  week_start: string
  week_label: string
  view_mode: "week" | "month"
  month: number | null
  year: number | null
  cases: FilterOption[]
  employees: FilterOption[]
  filters: Filters
  permissions: Permissions
}

const breadcrumbs: BreadcrumbItem[] = [{ title: "Calendar", href: "/calendar" }]

const DAY_NAMES_SUNDAY_START = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]

export default function CalendarIndex({
  week_days,
  week_start,
  week_label,
  view_mode,
  month,
  year,
  cases,
  employees,
  filters,
  permissions,
}: Props) {
  const [showCases, setShowCases] = useState(true)
  const [showMarketing, setShowMarketing] = useState(true)
  const [showAvailability, setShowAvailability] = useState(true)
  const [selectedCase, setSelectedCase] = useState<number | null>(
    filters.case_id,
  )
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(
    filters.employee_id,
  )

  const isMonth = view_mode === "month"

  const navigateWeek = (newWeekStart: string) => {
    router.get(
      calendarPath(),
      {
        week_start: newWeekStart,
        ...(selectedCase ? { case_id: selectedCase } : {}),
        ...(selectedEmployee ? { employee_id: selectedEmployee } : {}),
      },
      { preserveState: false },
    )
  }

  const navigateMonth = (m: number, y: number) => {
    router.get(
      calendarPath(),
      {
        v: "m",
        m,
        y,
        ...(selectedCase ? { case_id: selectedCase } : {}),
        ...(selectedEmployee ? { employee_id: selectedEmployee } : {}),
      },
      { preserveState: false },
    )
  }

  const shiftWeek = (days: number) => {
    navigateWeek(addDays(week_start, days))
  }

  const shiftMonth = (delta: number) => {
    if (!month || !year) return
    const d = new Date(year, month - 1 + delta, 1)
    navigateMonth(d.getMonth() + 1, d.getFullYear())
  }

  const handleUpdate = () => {
    if (isMonth && month && year) {
      navigateMonth(month, year)
    } else {
      navigateWeek(week_start)
    }
  }

  const handlePrev = () => {
    if (isMonth) shiftMonth(-1)
    else shiftWeek(-7)
  }

  const handleNext = () => {
    if (isMonth) shiftMonth(1)
    else shiftWeek(7)
  }

  // For monthly view, chunk days into weeks (rows of 7)
  const monthWeeks: CalendarDay[][] = []
  if (isMonth) {
    for (let i = 0; i < week_days.length; i += 7) {
      monthWeeks.push(week_days.slice(i, i + 7))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Calendar" />

      <div className="flex h-full flex-col">
        {/* Top bar: toggles + view switch + filters */}
        <div className="bg-hpi-pale-gray flex flex-wrap items-center gap-4 border-b border-gray-200 px-6 py-3">
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={showCases}
              onChange={(e) => setShowCases(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="font-medium text-gray-700">Cases</span>
          </label>
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={showMarketing}
              onChange={(e) => setShowMarketing(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="font-medium text-gray-700">Marketing</span>
          </label>
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={showAvailability}
              onChange={(e) => setShowAvailability(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="font-medium text-gray-700">Availability</span>
          </label>

          {permissions.can_filter && (
            <>
              <div className="ml-auto" />
              <select
                value={selectedCase ?? ""}
                onChange={(e) =>
                  setSelectedCase(
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="">All Cases</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <select
                value={selectedEmployee ?? ""}
                onChange={(e) =>
                  setSelectedEmployee(
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="">All Employees</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>

              <button
                onClick={handleUpdate}
                className="rounded bg-[#333] px-5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#555]"
              >
                Update
              </button>
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-6 bg-white px-6 py-4">
          <button
            onClick={handlePrev}
            className="text-gray-500 transition-colors hover:text-gray-800"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <h2 className="text-xl font-bold tracking-wide text-gray-800">
            {week_label}
          </h2>
          <button
            onClick={handleNext}
            className="text-gray-500 transition-colors hover:text-gray-800"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Calendar grid */}
        <div className="flex-1 overflow-auto px-4 pb-6">
          {isMonth ? (
            <>
              {/* Monthly view: desktop grid */}
              <div className="hidden grid-cols-7 bg-white xl:grid">
                {/* Day-of-week headers */}
                {DAY_NAMES_SUNDAY_START.map((name) => (
                  <div
                    key={`mhdr-${name}`}
                    className="bg-hpi-blue px-2 py-2 text-center text-white"
                  >
                    <div className="text-xs font-semibold tracking-wider">
                      {name}
                    </div>
                  </div>
                ))}

                {/* Month day cells */}
                {week_days.map((day) => (
                  <MonthDayCell
                    key={`mcell-${day.date}`}
                    day={day}
                    showCases={showCases}
                    showMarketing={showMarketing}
                    showAvailability={showAvailability}
                  />
                ))}
              </div>

              {/* Monthly view: mobile stacked */}
              <div className="space-y-4 xl:hidden">
                {week_days
                  .filter((d) => d.in_month)
                  .map((day) => (
                    <div
                      key={`mmobile-${day.date}`}
                      className="rounded border border-gray-300 bg-white"
                    >
                      <div className="bg-hpi-blue rounded-t px-3 py-2 text-white">
                        <span className="font-semibold">{day.day_name}</span>
                        <span className="ml-2 text-sm opacity-80">
                          {day.display_date}
                        </span>
                      </div>
                      <DayCellContent
                        day={day}
                        showCases={showCases}
                        showMarketing={showMarketing}
                        showAvailability={showAvailability}
                      />
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <>
              {/* Weekly view: desktop grid */}
              <div className="hidden grid-cols-7 bg-white xl:grid">
                {/* Day headers */}
                {week_days.map((day) => (
                  <div
                    key={`hdr-${day.date}`}
                    className="bg-hpi-blue px-2 py-2 text-center text-white"
                  >
                    <div className="text-xs font-semibold tracking-wider">
                      {day.day_name}
                    </div>
                    <div className="text-[11px] opacity-80">
                      {day.display_date}
                    </div>
                  </div>
                ))}

                {/* Day cells */}
                {week_days.map((day) => (
                  <DayCell
                    key={`cell-${day.date}`}
                    day={day}
                    showCases={showCases}
                    showMarketing={showMarketing}
                    showAvailability={showAvailability}
                  />
                ))}
              </div>

              {/* Weekly view: mobile stacked */}
              <div className="space-y-4 xl:hidden">
                {week_days.map((day) => (
                  <div
                    key={`mobile-${day.date}`}
                    className="rounded border border-gray-300 bg-white"
                  >
                    <div className="bg-hpi-blue rounded-t px-3 py-2 text-white">
                      <span className="font-semibold">{day.day_name}</span>
                      <span className="ml-2 text-sm opacity-80">
                        {day.display_date}
                      </span>
                    </div>
                    <DayCellContent
                      day={day}
                      showCases={showCases}
                      showMarketing={showMarketing}
                      showAvailability={showAvailability}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

// --- Day Cell (weekly view) ---

function DayCell({
  day,
  showCases,
  showMarketing,
  showAvailability,
}: {
  day: CalendarDay
  showCases: boolean
  showMarketing: boolean
  showAvailability: boolean
}) {
  return (
    <div
      className={`min-h-[300px] ${day.is_today ? "ring-2 ring-yellow-400 ring-inset" : ""}`}
    >
      <DayCellContent
        day={day}
        showCases={showCases}
        showMarketing={showMarketing}
        showAvailability={showAvailability}
      />
    </div>
  )
}

// --- Month Day Cell ---

function MonthDayCell({
  day,
  showCases,
  showMarketing,
  showAvailability,
}: {
  day: CalendarDay
  showCases: boolean
  showMarketing: boolean
  showAvailability: boolean
}) {
  const dimmed = !day.in_month

  return (
    <div
      className={`min-h-[120px] ${
        dimmed ? "bg-gray-50" : ""
      } ${day.is_today ? "ring-2 ring-yellow-400 ring-inset" : ""}`}
    >
      {/* Day number header */}
      <div
        className={`px-1.5 py-0.5 text-right text-xs font-medium ${
          dimmed ? "text-gray-300" : "text-gray-600"
        }`}
      >
        {parseLocalDate(day.date).getDate()}
      </div>
      {!dimmed && (
        <DayCellContent
          day={day}
          showCases={showCases}
          showMarketing={showMarketing}
          showAvailability={showAvailability}
        />
      )}
    </div>
  )
}

// --- Shared Day Content ---

function DayCellContent({
  day,
  showCases,
  showMarketing,
  showAvailability,
}: {
  day: CalendarDay
  showCases: boolean
  showMarketing: boolean
  showAvailability: boolean
}) {
  return (
    <div className="space-y-1.5 p-1.5">
      {/* Holidays */}
      {day.holidays.map((h) => (
        <HolidayBlock key={`hol-${h.id}`} holiday={h} />
      ))}

      {/* Case & Customer Schedules */}
      {showCases &&
        day.schedules
          .filter((s) => s.type === "case")
          .map((s) => <ScheduleBlock key={`sch-${s.id}`} schedule={s} />)}

      {/* Customer schedules (shown under marketing toggle like legacy) */}
      {showMarketing &&
        day.schedules
          .filter((s) => s.type === "customer")
          .map((s) => <ScheduleBlock key={`sch-${s.id}`} schedule={s} />)}

      {/* Marketing Logs */}
      {showMarketing &&
        day.marketing_logs.map((l) => (
          <MarketingLogBlock key={`ml-${l.id}`} log={l} />
        ))}

      {/* Marketing Activities */}
      {showMarketing &&
        day.marketing_activities.map((a) => (
          <MarketingActivityBlock key={`ma-${a.id}`} activity={a} />
        ))}

      {/* Availability */}
      {showAvailability && day.availability.length > 0 && (
        <div className="space-y-0.5 pt-1">
          {day.availability.map((a) => (
            <AvailabilityEntry key={`av-${a.user_id}`} entry={a} />
          ))}
        </div>
      )}
    </div>
  )
}

// --- Block Components ---

function HolidayBlock({ holiday }: { holiday: Holiday }) {
  return (
    <div className="border border-amber-200 bg-amber-50 px-2 py-1 text-xs">
      {holiday.person_name && (
        <span className="font-medium">{holiday.person_name}: </span>
      )}
      <span>{holiday.description}</span>
      {holiday.added_by && (
        <div className="text-[10px] text-gray-500">({holiday.added_by})</div>
      )}
    </div>
  )
}

function ScheduleBlock({ schedule }: { schedule: Schedule }) {
  const isCase = schedule.type === "case"
  const headerBg = isCase ? "bg-hpi-dark-blue" : "bg-hpi-dark-gray"
  const linkPath = isCase
    ? casePath({ id: schedule.case_id })
    : customerPath({ id: schedule.customer_id ?? 0 })
  const editPath = isCase
    ? `/cases/${schedule.case_id}/schedules/${schedule.id}/edit`
    : `/customers/${schedule.customer_id}/schedules/${schedule.id}/edit`

  return (
    <div className="overflow-hidden bg-gray-100 text-xs shadow-sm">
      {/* Header with case/customer name */}
      <h3
        className={`${headerBg} flex items-center gap-1 px-2 py-1 text-white`}
      >
        <Link
          href={linkPath}
          className="min-w-0 flex-1 truncate font-normal hover:underline"
        >
          {schedule.ref_name ?? "Untitled"}
        </Link>
        {schedule.can_edit && (
          <Link
            href={editPath}
            className="shrink-0 text-orange-300 hover:text-orange-100"
            title="Edit schedule"
          >
            <Pencil className="h-3 w-3" />
          </Link>
        )}
      </h3>

      {/* Team members bar */}
      {schedule.team.length > 0 && (
        <div className="bg-gray-200 px-2 py-0.5 text-[11px] leading-snug">
          {schedule.team.map((t, i) => (
            <span key={i}>
              {i > 0 && <span className="text-gray-400"> / </span>}
              {t.is_leader ? <b>{t.name}</b> : <span>{t.name}</span>}
            </span>
          ))}
        </div>
      )}

      {/* Body: time, title, notes */}
      <div className="space-y-0.5 px-2 py-1">
        {schedule.time_desc && (
          <div className="font-bold text-blue-700">{schedule.time_desc}</div>
        )}
        {schedule.title && (
          <div className="font-bold text-gray-800">{schedule.title}</div>
        )}
        {schedule.notes && (
          <div className="leading-snug whitespace-pre-line text-gray-600">
            {schedule.notes}
          </div>
        )}
      </div>
    </div>
  )
}

function MarketingLogBlock({ log }: { log: MarketingLog }) {
  return (
    <div className="overflow-hidden bg-gray-100 text-xs shadow-sm">
      <h3 className="bg-hpi-dark-gray truncate px-2 py-1 font-normal text-white">
        {log.customer_id ? (
          <Link
            href={customerLogPath(log.customer_id, log.id)}
            className="hover:underline"
            target="_blank"
          >
            {log.customer_name ?? "Unknown"}
          </Link>
        ) : (
          (log.customer_name ?? "Unknown")
        )}
      </h3>

      {log.employee_initials && (
        <div className="bg-gray-200 px-2 py-0.5 text-[11px]">
          {log.employee_initials}
        </div>
      )}

      <div className="space-y-0.5 px-2 py-1">
        {(log.log_type_name ?? log.contact) && (
          <div className="font-bold text-blue-700">
            {[log.log_type_name, log.contact].filter(Boolean).join(": ")}
          </div>
        )}
        {log.title && (
          <div className="font-bold text-gray-800">{log.title}</div>
        )}
        {log.notes && (
          <div className="leading-snug whitespace-pre-line text-gray-600">
            {log.notes}
          </div>
        )}
      </div>
    </div>
  )
}

function MarketingActivityBlock({ activity }: { activity: MarketingActivity }) {
  return (
    <div className="overflow-hidden bg-gray-100 text-xs shadow-sm">
      <h3 className="bg-hpi-dark-gray truncate px-2 py-1 font-normal text-white">
        <Link
          href={marketingActivityPath({ id: activity.id })}
          className="hover:underline"
          target="_blank"
        >
          {activity.title ?? "Untitled"}
        </Link>
      </h3>

      {activity.team.length > 0 && (
        <div className="bg-gray-200 px-2 py-0.5 text-[11px]">
          {activity.team.join(" / ")}
        </div>
      )}

      <div className="space-y-0.5 px-2 py-1">
        {activity.type_name && (
          <div className="font-bold text-blue-700">{activity.type_name}</div>
        )}
        {activity.notes && (
          <div className="leading-snug whitespace-pre-line text-gray-600">
            {activity.notes}
          </div>
        )}
      </div>
    </div>
  )
}

function AvailabilityEntry({ entry }: { entry: AvailabilityEntry }) {
  const bg = entry.available ? "bg-hpi-light-green" : "bg-hpi-light-red"

  return (
    <div className={`px-2 py-0.5 text-xs ${bg}`}>
      <span className="font-medium">{entry.name}</span>
      {entry.notes && (
        <div className="text-[10px] leading-snug whitespace-pre-line text-gray-600">
          {entry.notes}
        </div>
      )}
    </div>
  )
}
